import parser from "any-date-parser";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);
/** The zone the brokerage runs on, and the one every date-and-time field promises. */
export const EASTERN_TIMEZONE = "America/New_York";
const WALL_CLOCK_FORMAT = "YYYY-MM-DDTHH:mm:ss";
/**
 * Parse a date in virtually any format
 *
 * NOTE: This isn't great at handling timezones. It'll think
 * something like "2023-10-01T12:00:00Z" is in local time.
 * This method is best used when handling dates input by users,
 * NOT well-formatted dates returned from our database, which
 * can be handled directly by dayjs.
 *
 * @param dateString
 * @returns
 */
export const parseDate = (dateString) => {
    const date = parser.fromString(dateString, "en-US");
    if (!date.invalid) {
        // console.log("TEST: parsing date", dateString, date);
        return date;
    }
    return null;
};
/**
 * Parse text someone typed into a date-and-time field as an EASTERN wall clock.
 *
 * `parseDate` reads typed text in whatever zone the browser happens to sit in,
 * which is the wrong answer for a field that displays -- and promises --
 * Eastern: someone in Pacific who types "1:00 PM" means 1:00 PM Eastern, not
 * 4:00 PM. So we keep the wall-clock numbers the parser found and re-label them
 * in Eastern rather than converting them, which also makes blurring an
 * untouched field a no-op instead of shifting it by the zone offset each time.
 *
 * @param dateString Text as typed, in any format `parseDate` understands
 * @returns The instant that wall clock names in Eastern, or `null` if the text
 *   is not a date
 */
export const parseEasternDateTime = (dateString) => {
    const parsed = parseDate(dateString);
    if (!parsed) {
        return null;
    }
    const eastern = dayjs.tz(dayjs(parsed).format(WALL_CLOCK_FORMAT), EASTERN_TIMEZONE);
    return eastern.isValid() ? eastern.toDate() : null;
};
/**
 * Convert a UTC date string to local time
 *
 * @param date The date to convert
 * @param options Defines the timezone and format to which the date should be converted
 * @returns
 */
export const convertUtcDateString = (date, options) => {
    var _a, _b;
    const tz = (_a = options === null || options === void 0 ? void 0 : options.tz) !== null && _a !== void 0 ? _a : "America/New_York";
    const format = (_b = options === null || options === void 0 ? void 0 : options.format) !== null && _b !== void 0 ? _b : "YYYY-MM-DDTHH:mm:ssZ";
    const dateStr = typeof date === "string" ? date : date.toISOString();
    return dayjs.utc(dateStr).tz(tz).format(format);
};
